import React from 'react';
import {
  FileWarning,
  Mail,
  AlertTriangle,
  Globe,
  Network,
  Loader2,
  CheckCircle,
  Building2
} from 'lucide-react';
import { useIsMobile } from '../../../utils/isMobile';

interface ScanDetectionsProps {
  progress: {
    emails: number;
    vulnerabilities: number;
    ips: number;
    impersonation: number;
    leaks: number;
    similar_companies: number;
  };
  emailsCount: number;
  ipsCount: number;
  impersonationCount: number;
  leaksCount: number;
  vulnerabilitiesCount: number;
  isLoading: boolean;
  t: (key: string) => string;
  similarCompaniesCount: number;
}

const DETECTION_ITEMS = (p: ScanDetectionsProps) => [
  {
    key: 'leaks',
    label: p.t('scan.leaks'),
    icon: <FileWarning />,
    color: 'text-red-400',
    progress: p.progress.leaks,
    count: p.leaksCount,
    helper: p.t('scan.detectedBreaches'),
  },
  {
    key: 'emails',
    label: p.t('scan.emailCheck'),
    icon: <Mail />,
    color: 'text-yellow-400',
    progress: p.progress.emails,
    count: p.emailsCount,
    helper: p.t('scan.foundEmails'),
  },
  {
    key: 'vulnerabilities',
    label: p.t('scan.vulnScan'),
    icon: <AlertTriangle />,
    color: 'text-orange-400',
    progress: p.progress.vulnerabilities,
    count: p.vulnerabilitiesCount,
    helper: p.t('scan.securityIssues'),
  },
  {
    key: 'ips',
    label: p.t('scan.ipScan'),
    icon: <Network />,
    color: 'text-blue-400',
    progress: p.progress.ips,
    count: p.ipsCount,
    helper: p.t('scan.ipsFound'),
  },
  {
    key: 'impersonation',
    label: p.t('scan.impersonation'),
    icon: <Globe />,
    color: 'text-purple-400',
    progress: p.progress.impersonation,
    count: p.impersonationCount,
    helper: p.t('scan.impersonationFound'),
  },
  {
    key: 'similar_companies',
    label: p.t('scan.similarCompanies'),
    icon: <Building2 />,
    color: 'text-green-400',
    progress: p.progress.similar_companies,
    count: p.similarCompaniesCount ?? 0,
    helper: p.t('scan.foundSimilarCompanies'),
  },
];

const ScanDetections: React.FC<ScanDetectionsProps> = (props) => {
  const { isLoading, t } = props;
  const isMobile = useIsMobile();
  const items = DETECTION_ITEMS(props);

  // Grid classes: móvil 2 cols, desktop 1→2→3 cols
  const gridClasses = isMobile
    ? 'grid grid-cols-2 gap-3 mt-6 items-stretch'
    : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 items-stretch';

  // Clases responsivas para tamaños
  const cardPadding = isMobile ? 'p-3' : 'p-6';
  const iconSize    = isMobile ? 'w-8 h-8' : 'w-10 h-10';
  const labelSize   = isMobile ? 'text-sm' : 'text-lg';
  const countSize   = isMobile ? 'text-2xl' : 'text-3xl';
  const helperSize  = isMobile ? 'text-xs' : 'text-sm';

  return (
    <div className={gridClasses}>
      {items.map((item) => {
        const prog = Math.min(Math.max(item.progress, 0), 100);
        const loadingCard = isLoading && prog < 100;
        const doneCard = prog >= 100;

        // Clonar icono con tamaño y color adecuados
        const Icon = React.cloneElement(item.icon, {
          className: `${iconSize} ${item.color}`
        });

        // Contador sin prefijo '+'
        const displayCount: string | number = item.count;

        return (
          <div
            key={item.key}
            className={`
              relative bg-gray-700 rounded-lg ${cardPadding} shadow-lg
              transition-all hover:shadow-2xl
              flex flex-col justify-between h-full
              ${loadingCard ? 'animate-pulse' : ''}
            `}
          >
            {/* Indicadores absolutos */}
            {loadingCard && (
              <div className="absolute top-2 right-2 w-4 h-4 bg-white rounded-full animate-pulse" />
            )}
            {doneCard && (
              <div className="absolute top-2 right-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
              </div>
            )}

            {/* Bloque superior */}
            <div className="flex flex-col items-center gap-1">
              {Icon}
              <h3 className={`${labelSize} text-white font-semibold text-center`}>
                {item.label}
              </h3>
            </div>

            {/* Bloque medio: count + helper */}
            <div className="flex flex-col items-center">
              <p className={`${countSize} text-white font-bold text-center`}>
                {displayCount}
              </p>
              <p className={`${helperSize} text-gray-400 text-center`}>
                {item.helper}
              </p>
            </div>

            {/* Bloque inferior: barra de progreso */}
            <div className="mt-2 w-full bg-gray-600 rounded-full h-1.5">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  item.color.replace('text-', 'bg-')
                }`}
                style={{ width: `${prog}%` }}
              />
            </div>

            {/* Loader bajo la barra si sigue cargando */}
            {loadingCard && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <Loader2 className={`w-4 h-4 animate-spin ${item.color}`} />
                <p className={`${helperSize} text-gray-300`}>
                  {t('scan.loadingSection')}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ScanDetections;
